import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import usePageTitle from '../../hooks/usePageTitle';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

interface Transaction {
  _id: string;
  type: string;
  amount: number;
  status: string;
  description: string;
  createdAt: string;
}

const PAYMENT_METHODS = [
    { 
        id: 'bKash', 
        name: 'bKash', 
        icon: (
            <img 
                src="https://play-lh.googleusercontent.com/1CRcUfmtwvWxT2g-xJF8s9_btha42TLi6Lo-qVkVomXBb_citzakZX9BbeY51iholWs=w600-h300-pc0xffffff-pd" 
                alt="bKash" 
                className="h-12 w-auto object-contain"
            />
        ),
        bgColor: 'bg-[#e2136e]/5',
        borderColor: 'border-[#e2136e]/20',
        activeBorder: 'border-[#e2136e]'
    },
    { 
        id: 'Nagad', 
        name: 'Nagad', 
        icon: (
            <img 
                src="https://play-lh.googleusercontent.com/9ps_d6nGKQzfbsJfMaFR0RkdwzEdbZV53ReYCS09Eo5MV-GtVylFD-7IHcVktlnz9Mo" 
                alt="Nagad" 
                className="h-12 w-auto object-contain"
            />
        ),
        bgColor: 'bg-[#f6921e]/5',
        borderColor: 'border-[#f6921e]/20',
        activeBorder: 'border-[#f6921e]'
    },
    { 
        id: 'Rocket', 
        name: 'Rocket', 
        icon: (
            <img 
                src="https://play-lh.googleusercontent.com/sDY6YSDobbm_rX-aozinIX5tVYBSea1nAyXYI4TJlije2_AF5_5aG3iAS7nlrgo0lk8" 
                alt="Rocket" 
                className="h-12 w-auto object-contain" 
            />
        ),
        bgColor: 'bg-[#8c3494]/5',
        borderColor: 'border-[#8c3494]/20',
        activeBorder: 'border-[#8c3494]'
    },
    { 
        id: 'Card', 
        name: 'Card', 
        icon: (
           <div className="flex gap-2 items-center">
               <svg viewBox="0 0 50 30" className="h-5 w-auto">
                   <rect width="50" height="30" rx="4" fill="#1a1f71"/>
                   <text x="25" y="20" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial">VISA</text>
               </svg>
               <span className="text-gray-300">|</span>
               <svg viewBox="0 0 50 30" className="h-5 w-auto">
                   <circle cx="18" cy="15" r="10" fill="#eb001b" fillOpacity="0.8"/>
                   <circle cx="32" cy="15" r="10" fill="#f79e1b" fillOpacity="0.8"/>
               </svg>
           </div>
        ),
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        activeBorder: 'border-blue-600'
    },
];

const PRESET_AMOUNTS = [500, 1000, 2000, 5000, 10000];

const Wallet: React.FC = () => {
    usePageTitle('My Wallet');
    const { user } = useAuth();
    const [balance, setBalance] = useState(0);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState('');
    const [topUpMethod, setTopUpMethod] = useState('bKash');
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState('bKash');
    const [withdrawAccount, setWithdrawAccount] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchWalletData();
    }, []);

    const fetchWalletData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(getApiUrl('/api/wallet/balance'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBalance(response.data.balance);
            setTransactions(response.data.transactions);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching wallet data:', error);
            setLoading(false);
        }
    };

    const handleTopUp = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);
        
        // Simulate processing delay for better UX
        setTimeout(async () => {
            try {
                const token = localStorage.getItem('token');
                await axios.post(getApiUrl('/api/wallet/topup'), {
                    amount: Number(topUpAmount),
                    method: topUpMethod
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                setProcessing(false);
                setTopUpAmount('');
                setShowTopUpModal(false);
                fetchWalletData();
                // Optional: Show success toast/modal instead of alert
            } catch (error: any) {
                setProcessing(false);
                alert(error.response?.data?.message || 'Top up failed');
            }
        }, 2000);
    };

    const handleWithdrawAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        const isMFS = ['bKash', 'Nagad', 'Rocket'].includes(withdrawMethod);
        
        if (isMFS) {
            // MFS Logic: Force +880 prefix
            if (!value.startsWith('+880')) {
                value = '+880';
            }
            
            // Only allow digits after prefix
            const prefix = '+880';
            const suffix = value.substring(prefix.length).replace(/\D/g, '');
            value = prefix + suffix;
            
            // Limit to 14 characters total (+880 + 10 digits)
            if (value.length > 14) {
                value = value.substring(0, 14);
            }
        } else if (withdrawMethod === 'Card') {
            // Card Logic: XXXX XXXX XXXX XXXX
            const digits = value.replace(/\D/g, '').substring(0, 16);
            
            // Format with spaces
            const parts = [];
            for (let i = 0; i < digits.length; i += 4) {
                parts.push(digits.substring(i, i + 4));
            }
            value = parts.join(' ');
        }
        
        setWithdrawAccount(value);
    };

    const handleWithdrawMethodChange = (methodId: string) => {
        setWithdrawMethod(methodId);
        setWithdrawAccount('');
        if (['bKash', 'Nagad', 'Rocket'].includes(methodId)) {
            setWithdrawAccount('+880');
        }
    };

    const getCardType = (number: string) => {
        const clean = number.replace(/\D/g, '');
        if (clean.startsWith('4')) return 'VISA';
        if (/^5[1-5]/.test(clean) || /^2[2-7]/.test(clean)) return 'MASTERCARD';
        return null;
    };

    const handleWithdraw = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (Number(withdrawAmount) > balance) {
            alert('Insufficient balance');
            return;
        }

        setProcessing(true);
        
        // Simulate processing delay
        setTimeout(() => {
            setProcessing(false);
            setWithdrawAmount('');
            setWithdrawAccount('');
            setShowWithdrawModal(false);
            alert('Withdrawal request submitted successfully! Funds will be transferred within 24 hours.');
        }, 2000);
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-24 sm:pb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 font-display">My Wallet</h1>

            {/* Balance Card */}
            <div className="bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 rounded-2xl p-8 text-white shadow-xl mb-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-400 opacity-10 rounded-full -ml-24 -mb-24 blur-2xl"></div>
                
                <div className="relative z-10">
                    <p className="text-primary-100 text-sm font-medium uppercase tracking-wider mb-2">Available Balance</p>
                    <div className="flex items-baseline gap-2 mb-8">
                        <span className="text-5xl font-bold font-display">৳{balance.toLocaleString()}</span>
                        <span className="text-primary-200 text-xl">BDT</span>
                    </div>
                    
                    {user?.userType === 'customer' && (
                        <button 
                            onClick={() => setShowTopUpModal(true)}
                            className="group flex items-center gap-2 px-6 py-3 bg-white text-primary-700 font-bold rounded-xl hover:bg-primary-50 transition-all shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                        >
                            <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Top Up Balance
                        </button>
                    )}

                    {user?.userType === 'caregiver' && (
                        <button 
                            onClick={() => setShowWithdrawModal(true)}
                            className="group flex items-center gap-2 px-6 py-3 bg-white text-primary-700 font-bold rounded-xl hover:bg-primary-50 transition-all shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                        >
                            <svg className="w-5 h-5 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Withdraw Funds
                        </button>
                    )}
                </div>
            </div>

            {/* Transactions History */}
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Transaction History</h2>
                </div>
                
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {transactions.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <p className="text-lg font-medium text-gray-900">No transactions yet</p>
                            <p className="text-sm text-gray-500">Your wallet activity will appear here.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {transactions.map(tx => (
                                <div key={tx._id} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors group">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                                            tx.type === 'topup' || tx.type === 'payment_release' ? 'bg-green-50 text-green-600 group-hover:bg-green-100' : 
                                            tx.type === 'payment_hold' || tx.type === 'withdrawal' ? 'bg-orange-50 text-orange-600 group-hover:bg-orange-100' : 
                                            tx.type === 'refund' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-100' : 
                                            'bg-gray-50 text-gray-600'
                                        }`}>
                                            {tx.type === 'topup' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
                                            {tx.type === 'payment_hold' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
                                            {(tx.type === 'payment_release' || tx.type === 'withdrawal') && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                                            {tx.type === 'refund' && <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900 capitalize">{tx.description || tx.type.replace('_', ' ')}</p>
                                            <p className="text-xs text-gray-500 font-medium">{new Date(tx.createdAt).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} • {new Date(tx.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                    <span className={`font-bold text-lg ${
                                        tx.type === 'topup' || tx.type === 'payment_release' || tx.type === 'refund' 
                                            ? 'text-green-600' 
                                            : 'text-gray-900'
                                    }`}>
                                        {tx.type === 'topup' || tx.type === 'payment_release' || tx.type === 'refund' ? '+' : '-'}
                                        ৳{tx.amount.toLocaleString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Top Up Modal with Beautiful Design */}
            {showTopUpModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
                        {processing ? (
                            <div className="p-12 text-center flex flex-col items-center justify-center bg-white min-h-[400px]">
                                <div className="w-20 h-20 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin mb-6"></div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h3>
                                <p className="text-gray-500">Please verify the prompt on your phone...</p>
                            </div>
                        ) : (
                            <>
                                <div className="p-6 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900 font-display">Add Money</h3>
                                    <button 
                                        onClick={() => setShowTopUpModal(false)}
                                        className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                <form onSubmit={handleTopUp} className="p-6">
                                    <div className="mb-8">
                                        <label className="block text-sm font-semibold text-gray-700 mb-4 px-1">Select Method</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {PAYMENT_METHODS.map(method => (
                                                <button
                                                    key={method.id}
                                                    type="button"
                                                    onClick={() => setTopUpMethod(method.id)}
                                                    className={`relative p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-3 h-28 group ${
                                                        topUpMethod === method.id
                                                            ? `${method.bgColor} ${method.activeBorder} ring-1 ring-offset-2 ring-transparent`
                                                            : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-sm'
                                                    }`}
                                                >
                                                    {method.icon}
                                                    <span className={`text-sm font-medium ${topUpMethod === method.id ? 'text-gray-900' : 'text-gray-500'}`}>
                                                        {method.name}
                                                    </span>
                                                    {topUpMethod === method.id && (
                                                        <div className="absolute top-2 right-2 text-primary-600 bg-white rounded-full p-0.5 shadow-sm">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-8">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 px-1">Enter Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">৳</span>
                                            <input 
                                                type="number" 
                                                required
                                                min="1"
                                                value={topUpAmount}
                                                onChange={(e) => setTopUpAmount(e.target.value)}
                                                className="w-full pl-10 pr-4 py-4 text-2xl font-bold text-gray-900 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-300"
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                                            {PRESET_AMOUNTS.map(amount => (
                                                <button
                                                    key={amount}
                                                    type="button"
                                                    onClick={() => setTopUpAmount(amount.toString())}
                                                    className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-medium rounded-lg border border-gray-100 transition-colors whitespace-nowrap"
                                                >
                                                    ৳{amount.toLocaleString()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <button 
                                        type="submit"
                                        disabled={!topUpAmount || !topUpMethod}
                                        className="w-full py-4 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                                    >
                                        Proceed to Pay
                                    </button>
                                    
                                    <p className="text-center mt-4 text-xs text-gray-400 flex items-center justify-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                        Secure Payment Encrypted
                                    </p>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Withdraw Modal with Beautiful Design */}
            {showWithdrawModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
                        {processing ? (
                            <div className="p-12 text-center flex flex-col items-center justify-center bg-white min-h-[400px]">
                                <div className="w-20 h-20 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin mb-6"></div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Withdrawal</h3>
                                <p className="text-gray-500">Sending your request securely...</p>
                            </div>
                        ) : (
                            <>
                                <div className="p-5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900 font-display">Withdraw Funds</h3>
                                    <button 
                                        onClick={() => setShowWithdrawModal(false)}
                                        className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                <form onSubmit={handleWithdraw} className="p-5">
                                    <div className="mb-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 p-1">Available to Withdraw</label>
                                        <div className="px-4 py-3 bg-green-50 text-green-700 font-bold rounded-xl border border-green-100 flex justify-between items-center">
                                            <span>Balance</span>
                                            <span>৳{balance.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 px-1">Select Destination</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            {PAYMENT_METHODS.map(method => (
                                                <button
                                                    key={method.id}
                                                    type="button"
                                                    onClick={() => handleWithdrawMethodChange(method.id)}
                                                    className={`relative p-2 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-1 h-20 group ${
                                                        withdrawMethod === method.id
                                                            ? `${method.bgColor} ${method.activeBorder} ring-1 ring-offset-2 ring-transparent`
                                                            : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-sm'
                                                    }`}
                                                >
                                                    <div className="transform scale-75 origin-center">
                                                        {method.icon}
                                                    </div>
                                                    <span className={`text-xs font-medium ${withdrawMethod === method.id ? 'text-gray-900' : 'text-gray-500'}`}>
                                                        {method.name}
                                                    </span>
                                                    {withdrawMethod === method.id && (
                                                        <div className="absolute top-1 right-1 text-primary-600 bg-white rounded-full p-0.5 shadow-sm">
                                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 px-1">
                                            {withdrawMethod === 'Card' ? 'Card Number' : `${withdrawMethod} Number`}
                                        </label>
                                        <div className="relative">
                                            <input 
                                                type="text" 
                                                required
                                                value={withdrawAccount}
                                                onChange={handleWithdrawAccountChange}
                                                maxLength={withdrawMethod === 'Card' ? 19 : 14}
                                                className="w-full px-4 py-3 font-medium text-gray-900 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-400"
                                                placeholder={withdrawMethod === 'Card' ? '0000 0000 0000 0000' : '+880XXXXXXXXXX'}
                                            />
                                            {withdrawMethod === 'Card' && getCardType(withdrawAccount) && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 px-2 py-1 bg-gray-50 rounded border border-gray-200">
                                                    {getCardType(withdrawAccount) === 'VISA' && (
                                                        <svg viewBox="0 0 50 30" className="h-4 w-auto">
                                                            <rect width="50" height="30" rx="4" fill="#1a1f71"/>
                                                            <text x="25" y="20" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial">VISA</text>
                                                        </svg>
                                                    )}
                                                    {getCardType(withdrawAccount) === 'MASTERCARD' && (
                                                        <svg viewBox="0 0 50 30" className="h-4 w-auto">
                                                            <circle cx="18" cy="15" r="10" fill="#eb001b" fillOpacity="0.8"/>
                                                            <circle cx="32" cy="15" r="10" fill="#f79e1b" fillOpacity="0.8"/>
                                                        </svg>
                                                    )}
                                                    <span className="text-xs font-bold text-gray-600">{getCardType(withdrawAccount)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 px-1">Withdrawal Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg">৳</span>
                                            <input 
                                                type="number" 
                                                required
                                                min="500"
                                                max={balance}
                                                value={withdrawAmount}
                                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 text-xl font-bold text-gray-900 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-300"
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 px-1">Minimum withdrawal: ৳500</p>
                                    </div>

                                    <button 
                                        type="submit"
                                        disabled={!withdrawAmount || !withdrawMethod || !withdrawAccount || Number(withdrawAmount) > balance || (['bKash', 'Nagad', 'Rocket'].includes(withdrawMethod) && withdrawAccount.length !== 14) || (withdrawMethod === 'Card' && withdrawAccount.length < 16)}
                                        className="w-full py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                                    >
                                        Confirm Withdraw
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};


export default Wallet;
